import React, { useEffect, useRef, useCallback } from 'react';
import { MapRenderer } from '../pixi/MapRenderer';
import { useGameStore } from '../stores/gameStore';
import { useShardStore } from '../stores/shardStore';
import { contestNode } from '../game/socket';

function normalizeDensity(value: number, scale: number): number {
  if (value <= 0) return 0;
  return Math.min(1, Math.log10(1 + value / scale) / 3);
}

export function MapContainer() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<MapRenderer | null>(null);
  const { save, rates, isLoaded } = useGameStore();
  const { shard } = useShardStore();

  // Init Pixi on mount
  useEffect(() => {
    if (!canvasRef.current) return;
    const renderer = new MapRenderer(canvasRef.current);
    rendererRef.current = renderer;

    renderer.onNodeClick = (nodeId: number) => {
      contestNode(nodeId);
    };

    const handleResize = () => {
      const el = canvasRef.current;
      if (el && renderer) renderer.resize(el.clientWidth, el.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      renderer.destroy();
      rendererRef.current = null;
    };
  }, []);

  // Update theme when tier changes
  useEffect(() => {
    if (!rendererRef.current) return;
    rendererRef.current.setTheme(save.tier === 1 ? 'planet' : 'solar');
  }, [save.tier]);

  // Push density data to renderer every 500ms (don't need 60fps for map updates)
  useEffect(() => {
    if (!rendererRef.current || !isLoaded) return;

    // Scale thresholds: what count = "dense" on the map
    const primaryScale = save.tier === 1 ? 1e6 : 1e15;
    const knowledgeScale = save.tier === 1 ? 1e4 : 1e13;
    const energyScale = save.tier === 1 ? 1e4 : 1e13;
    const pvpScale = save.tier === 1 ? 500 : 5e12;

    const data = {
      production: normalizeDensity(save.resources.primary, primaryScale),
      knowledge: normalizeDensity(save.resources.knowledge, knowledgeScale),
      energy: normalizeDensity(save.resources.energy, energyScale),
      pvp: normalizeDensity(save.resources.pvp, pvpScale),
      tier: save.tier,
    };

    rendererRef.current.updateDensity(data);

    // Update nodes if shard exists
    if (shard) {
      const nodes = shard.nodes.map((n) => ({
        id: n.id,
        x: n.position.x,
        y: n.position.y,
        ownerId: n.ownerId,
        controlScore: n.controlScore,
        myId: save.playerId,
      }));
      rendererRef.current.updateNodes(nodes);
    }
  }, [save.resources, save.tier, save.playerId, shard, isLoaded]);

  return (
    <div style={styles.container}>
      <canvas ref={canvasRef} style={styles.canvas} />
      {/* Production rates overlay */}
      {isLoaded && (
        <div style={styles.ratesOverlay}>
          <div style={styles.rateItem}>
            <span style={{ color: '#4af0c4' }}>⬡</span>
            <span style={styles.rateLabel}>{formatDensityPct(save.resources.primary, save.tier === 1 ? 1e9 : 1e18)} saturated</span>
          </div>
          <div style={styles.rateItem}>
            <span style={{ color: '#a78bfa' }}>◈</span>
            <span style={styles.rateLabel}>{formatDensityPct(save.resources.knowledge, save.tier === 1 ? 1e7 : 1e16)} coverage</span>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDensityPct(value: number, max: number): string {
  const pct = Math.min(100, (value / max) * 100);
  if (pct < 0.01) return '<0.01%';
  if (pct < 1) return pct.toFixed(2) + '%';
  if (pct < 100) return pct.toFixed(1) + '%';
  return '100%';
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'relative',
    flex: 1,
    overflow: 'hidden',
    background: '#0a0e1a',
  },
  canvas: {
    display: 'block',
    width: '100%',
    height: '100%',
  },
  ratesOverlay: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    pointerEvents: 'none',
  },
  rateItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    background: 'rgba(10,14,26,0.7)',
    padding: '2px 6px',
    borderRadius: 3,
  },
  rateLabel: {
    fontSize: 10,
    color: '#4a6080',
  },
};
